import React from 'react';
import * as auth from '../api/auth';

export type AuthStatus = 'checking' | 'authed' | 'guest';

export interface AuthUser {
  email: string;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  refresh: () => Promise<AuthStatus>;
  markAuthed: () => void;
  markGuest: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>('checking');
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const inFlight = React.useRef<Promise<AuthStatus> | null>(null);

  const refresh = React.useCallback(async (): Promise<AuthStatus> => {
    if (inFlight.current) {
      return inFlight.current;
    }
    setStatus(prev => (prev === 'checking' ? prev : 'checking'));
    const promise = auth
      .me()
      .then((data: any): AuthStatus => {
        const email = data?.user?.email || '';
        setUser(email ? { email } : null);
        return 'authed';
      })
      .catch((): AuthStatus => {
        setUser(null);
        return 'guest';
      })
      .then(next => {
        setStatus(next);
        return next;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = promise;
    return promise;
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const markAuthed = React.useCallback(() => setStatus('authed'), []);
  const markGuest = React.useCallback(() => {
    setUser(null);
    setStatus('guest');
  }, []);

  const value = React.useMemo(
    () => ({ status, user, refresh, markAuthed, markGuest }),
    [status, user, refresh, markAuthed, markGuest]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
